import type { PostHog } from "posthog-js";
import { getAppVersion } from "../utils/version.ts";

export const EVENTS = Object.freeze({
  BIDROW_TASKS_PASTED: "bidrow_tasks_pasted",
  BIDROWS_SPLITTED: "bidrows_splitted",
  AI_VOICE_RECORDED: "ai_voice_recorded",
  PROJECT_CREATED: "project_created",
  PROJECT_UPDATED: "project_updated",
  PROJECT_TAG_CREATED: "project_tag_created",
  PROJECT_TAGS_UPDATED: "project_tags_updated",
  SSO_RETRY_CLICKED: "sso_retry_clicked",
  PURCHASE_SIDE_PANEL_OPENED: "purchase_side_panel_opened",
  SUPPLIER_QUOTE_IMPORT_SUBMITTED: "supplier_quote_import_submitted",
  SELECTED_COMMERCIAL_ACTIVITY_PERIOD_CHANGED: "selected_commercial_activity_period_changed",
  BID_IMPORT_REVIEW_ENDED: "bid_import_review_ended",
  BIDROW_FAST_ADDED: "bidrow_fast_added",
  BIDROW_PERCENTAGE_ADDED: "bidrow_percentage_added",
  BIDROW_ADJUSTED_PURCHASE_ADDED: "bidrow_adjusted_purchase_added",
});

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

// posthog-js (~160 KB) is loaded on demand to keep it out of the initial bundle.
// Calls made before it resolves are chained on the promise, so they keep their order.
// A failed load (ad blocker, offline) resolves to null so analytics never throws.
let posthogPromise: Promise<PostHog | null> | null = null;

const withPosthog = async (use: (posthog: PostHog) => void): Promise<void> => {
  const posthog = await posthogPromise;
  if (posthog) {
    use(posthog);
  }
};

export const initPosthog = () => {
  // Keep the handlers on import() and don't destructure the module: Vite then leaves them inside its preload
  // wrapper. Otherwise a failed load dispatches `vite:preloadError`, whose handler reloads the page.
  posthogPromise = import("posthog-js").then(
    (module) => {
      initLoadedPosthog(module.posthog);
      return module.posthog;
    },
    () => null,
  );
};

const initLoadedPosthog = (posthog: PostHog): void => {
  posthog.init("phc_X4HKHqYQ9eTpRa0AkR7qaItkllOAD5OIjL5AFi2Ynyx", {
    api_host: "https://app.grafit.fr/ingest",
    ui_host: "https://eu.i.posthog.com",
    person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
  });

  posthog.register_once({
    app_version: getAppVersion(),
  });
};

interface PosthogUser {
  id: string;
  email?: string;
}

export const setPosthogUser = (user: PosthogUser | null) => {
  void withPosthog((posthog) => {
    if (!user) {
      posthog.reset();
      return;
    }

    posthog.identify(user.id, { email: user.email });
  });
};

/**
 * Use `EVENTS` only. Event names must be snake_case, past tense, and business-oriented.
 * Put context in properties, not in the event name. Prefer reusing an existing event with a property over creating a new event.
 */
export const trackEvent = (eventName: EventName, properties?: Record<string, unknown>) => {
  void withPosthog((posthog) => posthog.capture(eventName, properties));
};
